import cx from 'classnames';
import { memo, useCallback, useMemo } from 'react';
import { ClanName, DivTooltip, Realm, TableBodyCell, TableRow, Tooltip, TooltipBody } from '@wg/wows-react-uikit';

import settings from '~/settings';
import { SEASON_TYPES } from '~/constants';
import { isoToFormattedLocalDateTime } from '~/helpers/datetime';
import { t } from '~/helpers/localization';
import { lastSeason } from '~/helpers/seasons';

import styles from './ClanBattlesTable.scss';

const RESULT_TOOLTIP_TEXTS = {
  promotion: {
    league: t('Борьба за переход в следующую лигу'),
    division: t('Борьба за переход в следующий дивизион'),
  },
  demotion: {
    league: t('Борьба за сохранение лиги'),
    division: t('Борьба за сохранение дивизиона'),
  },
  result: {
    victory: t('Победа'),
    defeat: t('Поражение'),
    draw: t('Ничья'),
  },
} as const;

type ResultType = 'victory' | 'defeat' | 'draw';
type ArrowType = 'promotion' | 'demotion' | 'result' | '';
type TargetType = 'promotion' | 'demotion' | 'result' | '';

interface ClanBattlesTableItemProps {
  arrow: ArrowType;
  battleId: number;
  date: string;
  enemyName: string;
  enemyRealm: string;
  enemyTag: string;
  enemyTagColor: string;
  enemyTeamNumber: number;
  isActive: boolean;
  map: string;
  progress: unknown[];
  ratingDelta: number;
  result: ResultType;
  seasonType?: SEASON_TYPES;
  target: TargetType;
}

const ClanBattlesTableItem = ({
  arrow,
  date,
  enemyName,
  enemyRealm,
  enemyTag,
  enemyTagColor,
  isActive,
  map,
  progress,
  ratingDelta,
  result,
  seasonType,
  target,
}: ClanBattlesTableItemProps) => {
  const isBrawl = seasonType === SEASON_TYPES.BRAWL;

  const renderPromotionOrDemotionIcons = useCallback(
    (a: ArrowType, r: ResultType) => {
      const mediaPath = settings.mediaPath;

      const icons = lastSeason?.additional_icons?.stars ?? {};
      const resultPostfix = r === 'victory' ? 'victory' : 'defeat';

      const isArrow = a === 'promotion' || a === 'demotion';
      if (!isArrow) {
        return null;
      }

      if (progress.length) {
        const key = `${a}_${resultPostfix}`;
        return <img src={mediaPath + (icons[key] ?? '')} alt="" />;
      }

      return <div className={cx(styles.arrowIcon, styles[a])} />;
    },
    [progress.length],
  );

  const tooltipBody = useMemo(() => {
    let mainText = '';
    let resultText = RESULT_TOOLTIP_TEXTS.result[result] ?? '';

    if (target && arrow) {
      const arrowTexts = RESULT_TOOLTIP_TEXTS[arrow as 'promotion' | 'demotion' | 'result'];
      mainText = arrowTexts && target in arrowTexts ? (arrowTexts[target as keyof typeof arrowTexts] as string) : '';

      if (!progress.length) {
        resultText = '';
      }
    }

    return (
      <Tooltip>
        <TooltipBody>
          {mainText}
          <br style={{ display: mainText ? 'auto' : 'none' }} />
          {resultText}
        </TooltipBody>
      </Tooltip>
    );
  }, [arrow, progress.length, result, target]);

  const clanNameTooltip = useMemo(() => {
    return <TooltipBody>{t('Цвет клан тега на момент окончания боя')}</TooltipBody>;
  }, []);

  const enemyRealmCell = useMemo(() => {
    if (!settings.ladder.isCrossRealmLadderEnabled) {
      return null;
    }

    return (
      <TableBodyCell modify="left">
        <Realm realm={enemyRealm} realmMap={settings.realmsMap} />
      </TableBodyCell>
    );
  }, [enemyRealm]);

  const resultCell = useMemo(() => {
    if (isBrawl) {
      return (
        <TableBodyCell modify="center">
          <div
            className={cx(styles[result], styles.numResult, {
              [styles.show]: true,
            })}
          >
            {RESULT_TOOLTIP_TEXTS.result[result]}
          </div>
        </TableBodyCell>
      );
    }

    return (
      <TableBodyCell modify="center">
        <DivTooltip tooltipBody={tooltipBody}>
          {renderPromotionOrDemotionIcons(arrow, result)}
          <div
            className={cx(styles[result], styles.numResult, {
              [styles.show]: !arrow,
            })}
          >
            {ratingDelta}
          </div>
        </DivTooltip>
      </TableBodyCell>
    );
  }, [arrow, isBrawl, ratingDelta, renderPromotionOrDemotionIcons, result, tooltipBody]);

  return (
    <TableRow isHovering isActive={isActive}>
      <TableBodyCell modify="left">{isoToFormattedLocalDateTime(date)}</TableBodyCell>
      {resultCell}
      <TableBodyCell modify="left">{map}</TableBodyCell>
      {enemyRealmCell}
      <TableBodyCell modify="basis">
        <DivTooltip tooltipBody={clanNameTooltip}>
          <ClanName
            clanName={enemyName}
            clanTag={enemyTag}
            clanColor={enemyTagColor}
            isBrawl={isBrawl}
            seasonType={seasonType}
          />
        </DivTooltip>
      </TableBodyCell>
    </TableRow>
  );
};

export default memo(ClanBattlesTableItem);
